import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShellLayout } from '@ooh/ui/components/app-shell-layout';
import type { AppShellNavItem, AppShellNavLinkProps } from '@ooh/ui/components/app-shell-layout';
import { Separator } from '@ooh/ui/components/separator';
import { Link } from '../plugs/link';
import { buildNavEntries } from './build-nav-entries';
import type { NavAccess } from './build-nav-entries';
import { LanguageSwitcher } from './language-switcher';
import type { SupportedLanguage } from './language-switcher';
import { NotificationBellButton } from './notification-bell-button';
import { UserMenu } from './user-menu';

const BRAND_MARK = 'RW';

export interface AppFrameProps {
  pageTitle: string;
  activePath: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  access: NavAccess;
  userName?: string;
  userEmail?: string;
  onLogout: () => void;
  onChangeLanguage?: (lng: SupportedLanguage) => void;
  includeDraftEntries?: boolean;
  renderNavLink?: (item: AppShellNavItem, linkProps: AppShellNavLinkProps) => ReactNode;
  notificationBell?: ReactNode;
  mainStyle?: CSSProperties;
  children: ReactNode;
}

function renderPluggedLink(item: AppShellNavItem, linkProps: AppShellNavLinkProps) {
  return <Link to={item.path} {...linkProps} />;
}

export function AppFrame({
  pageTitle,
  activePath,
  collapsed,
  onToggleCollapsed,
  access,
  userName,
  userEmail,
  onLogout,
  onChangeLanguage,
  includeDraftEntries,
  renderNavLink = renderPluggedLink,
  notificationBell,
  mainStyle,
  children,
}: AppFrameProps) {
  const { t, i18n } = useTranslation('common');

  return (
    <AppShellLayout
      brandMark={BRAND_MARK}
      appName={t('appName')}
      navItems={buildNavEntries(t, access, { includeDraftEntries })}
      activePath={activePath}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      labels={{
        sidebar: t('sidebar.ariaLabel'),
        mainMenu: t('sidebar.mainMenu'),
        expand: t('sidebar.expand'),
        collapse: t('sidebar.collapse'),
      }}
      renderLink={renderNavLink}
      pageTitle={pageTitle}
      mainStyle={mainStyle}
      topBarActions={
        <>
          {notificationBell ?? <NotificationBellButton label={t('notifications.bellLabel')} />}
          <Separator orientation="vertical" className="h-5" />
          <LanguageSwitcher
            currentLanguage={i18n.language}
            switchLabel={t('language.switchLabel')}
            plLabel={t('language.pl')}
            enLabel={t('language.en')}
            onSelect={onChangeLanguage ?? ((lng) => void i18n.changeLanguage(lng))}
          />
          <Separator orientation="vertical" className="h-5" />
          <UserMenu
            userName={userName ?? t('nav.user')}
            userEmail={userEmail ?? ''}
            logoutLabel={t('nav.logout')}
            onLogout={onLogout}
          />
        </>
      }
    >
      {children}
    </AppShellLayout>
  );
}
